import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
    Avatar,
    Box,
    Button,
    Collapse,
    Divider,
    Drawer,
    InputAdornment,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    TextField,
    Typography,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ArticleIcon from '@mui/icons-material/Article';
import CampaignIcon from '@mui/icons-material/Campaign';
import CollectionsBookmarkIcon from '@mui/icons-material/CollectionsBookmark';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import OutdoorGrillIcon from '@mui/icons-material/OutdoorGrill';
import SearchIcon from '@mui/icons-material/Search';

import { RECIPE_TYPES } from '../constants/Constants';
import announcements from '../announcements';
import { fetchDrafts } from '../utils/draftsApi';
import { getActiveRecipes } from '../utils/cookSession';

const STORAGE_KEY = 'dismissedAnnouncement';

const NavDrawer = ({ open, onClose }) => {
    const navigate = useNavigate();
    const location = useLocation();

    const [searchValue, setSearchValue] = useState('');
    const [typesOpen, setTypesOpen] = useState(false);
    const [draftCount, setDraftCount] = useState(0);
    const [cookCount, setCookCount] = useState(0);

    useEffect(() => {
        const update = () => setCookCount(getActiveRecipes().length);
        update();
        window.addEventListener('cookSessionChanged', update);
        return () => window.removeEventListener('cookSessionChanged', update);
    }, []);
    const hasNewAnnouncement = announcements.enabled &&
        localStorage.getItem(STORAGE_KEY) !== announcements.current;

    useEffect(() => {
        setTypesOpen(false);
        onClose();
    }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (open) fetchDrafts().then(d => setDraftCount(d.length)).catch(() => {});
    }, [open]);

    const handleClose = () => {
        onClose();
        setTypesOpen(false);
    };

    const handleSearch = (e) => {
        if (e.key === 'Enter' && searchValue.trim()) {
            navigate(`/search/${encodeURIComponent(searchValue.trim())}`);
            setSearchValue('');
            setTimeout(handleClose, 100);
        }
    };

    const drawerUser = JSON.parse(localStorage.getItem('user')) || {};
    const drawerInitials = `${drawerUser.first_name?.[0] ?? ''}${drawerUser.last_name?.[0] ?? ''}`.toUpperCase();
    const drawerDisplayName = drawerUser.display_name || `${drawerUser.first_name ?? ''} ${drawerUser.last_name ?? ''}`.trim();

    return (
        <Drawer anchor="left" open={open} onClose={handleClose}>
            <Box sx={{ width: 260, display: 'flex', flexDirection: 'column', height: '100%' }}>

                {/* Drawer header */}
                <Box sx={{ px: 2, py: 2, bgcolor: 'primary.main', color: 'primary.contrastText', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar
                        src={drawerUser.avatar_url || undefined}
                        sx={{ width: 36, height: 36, bgcolor: 'primary.light', fontSize: '0.875rem', flexShrink: 0 }}
                    >
                        {drawerInitials}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }} noWrap>
                            {drawerDisplayName}
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.8 }} noWrap>
                            Sposito Family Cookbook
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ flex: 1, overflow: 'auto' }}>
                    {/* Search */}
                    <Box sx={{ px: 2, py: 1.5 }}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Search recipes…"
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            onKeyDown={handleSearch}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>

                    {/* Add Recipe */}
                    <Box sx={{ px: 2, pb: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Button
                            fullWidth
                            variant="contained"
                            startIcon={<AddCircleOutlineIcon />}
                            onClick={() => { handleClose(); navigate('/addrecipe'); }}
                        >
                            Add Recipe
                        </Button>
                        {draftCount > 0 && (
                            <Button
                                fullWidth
                                variant="outlined"
                                size="small"
                                startIcon={<ArticleIcon fontSize="small" />}
                                onClick={() => { handleClose(); navigate('/addrecipe?loadOldest=1'); }}
                                sx={{ color: 'text.secondary', borderColor: 'divider' }}
                            >
                                My Drafts ({draftCount})
                            </Button>
                        )}
                        <Button
                            fullWidth
                            variant="outlined"
                            size="small"
                            startIcon={<OutdoorGrillIcon fontSize="small" />}
                            onClick={() => { handleClose(); navigate('/cook'); }}
                            sx={{ color: 'text.secondary', borderColor: 'divider' }}
                        >
                            {cookCount > 0 ? `Cooking Session (${cookCount})` : 'Start Cooking Session'}
                        </Button>
                    </Box>

                    <Divider />

                    {/* Browse section */}
                    <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
                        <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1.5, fontSize: 10 }}>
                            Browse
                        </Typography>
                    </Box>

                    <List disablePadding>
                        <ListItemButton onClick={() => { handleClose(); navigate('/collections'); }}>
                            <ListItemIcon sx={{ minWidth: 36 }}>
                                <CollectionsBookmarkIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText primary="Collections" />
                        </ListItemButton>

                        <ListItemButton onClick={() => setTypesOpen(o => !o)}>
                            <ListItemIcon sx={{ minWidth: 36 }}>
                                <MenuBookIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText primary="Recipe Types" />
                            {typesOpen ? <ExpandLess /> : <ExpandMore />}
                        </ListItemButton>
                        <Collapse in={typesOpen} timeout="auto" unmountOnExit>
                            <List component="div" disablePadding>
                                {RECIPE_TYPES.map((item) => (
                                    <Link
                                        key={item.key}
                                        to={`/types/${item.key}`}
                                        style={{ textDecoration: 'none', color: 'inherit' }}
                                    >
                                        <ListItemButton sx={{ pl: 5 }} onClick={handleClose}>
                                            <ListItemText
                                                primary={item.title}
                                                primaryTypographyProps={{ variant: 'body2' }}
                                            />
                                        </ListItemButton>
                                    </Link>
                                ))}
                            </List>
                        </Collapse>
                    </List>
                </Box>

                {/* Bottom: What's New */}
                <Box>
                    <Divider />
                    <ListItemButton onClick={() => { handleClose(); navigate('/whats-new'); }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                            <CampaignIcon fontSize="small" color={hasNewAnnouncement ? 'primary' : 'inherit'} />
                        </ListItemIcon>
                        <ListItemText
                            primary="What's New"
                            primaryTypographyProps={{
                                color: hasNewAnnouncement ? 'primary' : 'text.secondary',
                                fontWeight: hasNewAnnouncement ? 700 : 400,
                            }}
                        />
                        {hasNewAnnouncement && (
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main' }} />
                        )}
                    </ListItemButton>
                </Box>

            </Box>
        </Drawer>
    );
};

export default NavDrawer;
